import { FC, PropsWithChildren, useMemo, MouseEvent } from "react";
import classNames from "classnames";
import ActiveLink from "components/base/active-link/ActiveLink";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import SportIcon from "components/base/sport-icon/SportIcon";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import styles from "./navigation-bar.module.sass";

const ICONS_PATH = `/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}`;
type Props = {
    type: "button" | "link";
    active: boolean;
    variant: "base" | "sidebar";
    isSelected: boolean;
    onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLDivElement>) => void;
    translationData?: {
        tid: string;
        namespace?: TranslationScopes;
    };
    sportIconId?: number;
    svgIconSrc?: string;
    count?: number;
    text?: string;
    hasFavorite?: boolean;
    showGamesCount?: boolean;
    href?: string | (() => string);
};
export const NavigationBarItem: FC<PropsWithChildren<Props>> = ({
    type,
    active,
    isSelected,
    hasFavorite,
    showGamesCount,
    translationData,
    sportIconId,
    svgIconSrc,
    count,
    text,
    variant,
    href,
    onClick,
    children,
}) => {
    const renderDefaultContent = useMemo(
        () => (
            <>
                {hasFavorite && (
                    <Svg src={`${ICONS_PATH}/favourites.svg`} className={styles.hasFavorite} />
                )}
                {showGamesCount && <span className={styles.gamesCount}>{count}</span>}
                {sportIconId && <SportIcon sportId={sportIconId} />}
                {svgIconSrc && (
                    <Svg
                        src={`${ICONS_PATH}/${svgIconSrc}.svg`}
                        className="svg-sport-icon"
                        loader
                    />
                )}
                <span className={styles.navItemName}>
                    {translationData ? (
                        <Translate
                            tid={translationData.tid}
                            namespace={translationData.namespace}
                        />
                    ) : (
                        text
                    )}
                </span>
            </>
        ),
        [hasFavorite, showGamesCount, count, sportIconId, text, translationData, svgIconSrc],
    );
    const renderType = useMemo(() => {
        switch (type) {
            case "button":
                return (
                    <div
                        className={classNames(
                            active && styles.active,
                            isSelected && styles.selected,
                            styles.navItem,
                            styles[variant],
                        )}
                        onClick={onClick}>
                        {children ? children : renderDefaultContent}
                    </div>
                );
            default:
                return (
                    <ActiveLink
                        href={{
                            pathname: typeof href === "function" ? href() : href,
                        }}
                        forceActive={active}
                        activeClassName={styles.active}
                        className={classNames(
                            isSelected && styles.selected,
                            styles.navItem,
                            styles[variant],
                        )}
                        onClick={onClick}>
                        {children ? children : renderDefaultContent}
                    </ActiveLink>
                );
        }
    }, [type, active, isSelected, variant, onClick, children, href, renderDefaultContent]);

    return renderType;
};
